import { Injectable } from '@angular/core';
import {
  CanActivate,
  Router,
  UrlTree,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import { of, Observable } from 'rxjs';
import { take, switchMap, map, catchError } from 'rxjs/operators';
import { UserAuthService } from '@services/user-auth.service';
 
@Injectable({
  providedIn: 'root',
})
export class InvalidEntriesGuard implements CanActivate {
  constructor(
    private readonly userAuthService: UserAuthService,
    private readonly router: Router,
  ) {}
 
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean | UrlTree | Observable<boolean | UrlTree> {
    const targetUrl = state.url ?? '';
    // 1. Sanitize the path immediately to strip query params (?...) and hashes (#...)
    const basePath = targetUrl.split('?')[0].split('#')[0] || '/';
    const normalizedPath = basePath === '' ? '/' : basePath;

    // 2. Allow accessing profile page unconditionally
    if (normalizedPath === '/myProfile') {
      return true;
    }
 
    // use cached profile if available; otherwise fetch once from backend
    return this.userAuthService.userProfileData$.pipe(
      take(1),
      switchMap((profile) =>
        profile != null ? of(profile) : this.userAuthService.fetchUserProfile().pipe(take(1)),
      ),
      catchError(() => of(null)),
      map((profile): boolean | UrlTree => {
        const hasRegion =
          !!profile &&
          ((profile.regionId != null && Number(profile.regionId) > 0) ||
            (typeof profile.regionId === 'string' && (profile.regionId as string).trim().length > 0));
        const hasRoute =
          !!profile &&
          ((profile.routeId != null && Number(profile.routeId) > 0) ||
            (typeof profile.routeId === 'string' && (profile.routeId as string).trim().length > 0));
        const validPublicRoutes = ['/', '/myProfile', '/home'];
        const isValidPublicRoute = validPublicRoutes.includes(normalizedPath);

        const segments = normalizedPath.split('/').filter(Boolean);
        const validRouteRoots = ['cat', 'fsr', 'admin'];
        const isValidRoute = isValidPublicRoute || (segments.length > 0 && validRouteRoots.includes(segments[0]));
        // Scenario A: Completely invalid/unmapped URL entr
        if (!isValidRoute) {
          if (hasRegion && hasRoute) {
            return this.router.createUrlTree(['/home']);
          }
          return this.router.createUrlTree(['/myProfile']);
        }
        // Scenario B: Route is valid, now check permissions/data completeness
        if (hasRegion && hasRoute) {
          if (targetUrl === '' || targetUrl === '/' ) {
            return this.router.createUrlTree(['/home']);
          }
          return true;
        }
        // Scenario C: Route is valid but profile is missing configuration data
        return this.router.createUrlTree(['/myProfile']);
      }),
    );
  }
}
 